import Phaser from 'phaser';
import { initializeStorage } from '@/storage/storage-init';
import { initAudio } from '@/audio/audio-manager';
import { NEON_FONTS } from '@/config/colors';

const LOADING_FONT = NEON_FONTS.TITLE;
const LOADING_BODY_FONT = NEON_FONTS.BODY;
const BAR_WIDTH_RATIO = 0.6;
const BAR_HEIGHT = 16;
const BAR_BG_COLOR = 0x0a0a30;
const BAR_FILL_COLOR = 0x00e5ff;
const BAR_BORDER_COLOR = 0x1a2a4a;
const MIN_DISPLAY_MS = 800;

export class BootScene extends Phaser.Scene {
  private progressBar: Phaser.GameObjects.Graphics | null = null;
  private progressBg: Phaser.GameObjects.Graphics | null = null;
  private percentText: Phaser.GameObjects.Text | null = null;
  private loadingText: Phaser.GameObjects.Text | null = null;
  private assetText: Phaser.GameObjects.Text | null = null;
  private loadStartTime = 0;

  constructor() {
    super({ key: 'BootScene' });
  }

  preload(): void {
    this.loadStartTime = Date.now();
    initializeStorage();
    this.createLoadingScreen();
    this.loadGameAssets();
  }

  create(): void {
    const elapsed = Date.now() - this.loadStartTime;
    const remaining = Math.max(0, MIN_DISPLAY_MS - elapsed);

    initAudio(this.game);

    this.time.delayedCall(remaining, () => {
      this.cleanupLoadingScreen();
      this.scene.start('MenuScene');
    });
  }

  private createLoadingScreen(): void {
    const { width, height } = this.cameras.main;
    const centerX = width / 2;
    const centerY = height / 2;
    const barWidth = width * BAR_WIDTH_RATIO;
    const barX = (width - barWidth) / 2;
    const barY = centerY;

    // "LOADING..." title
    this.loadingText = this.add.text(centerX, centerY - 50, 'LOADING...', {
      fontFamily: LOADING_FONT,
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#00E5FF',
      align: 'center',
      shadow: { offsetX: 0, offsetY: 0, color: '#00E5FF', blur: 10, fill: true },
    }).setOrigin(0.5);

    // Progress bar background with border
    this.progressBg = this.add.graphics();
    this.progressBg.fillStyle(BAR_BG_COLOR, 1);
    this.progressBg.fillRect(barX - 2, barY - 2, barWidth + 4, BAR_HEIGHT + 4);
    this.progressBg.lineStyle(1, BAR_BORDER_COLOR, 1);
    this.progressBg.strokeRect(barX - 2, barY - 2, barWidth + 4, BAR_HEIGHT + 4);

    // Progress bar fill
    this.progressBar = this.add.graphics();

    // Percentage text
    this.percentText = this.add.text(centerX, barY + BAR_HEIGHT + 20, '0%', {
      fontFamily: LOADING_FONT,
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      align: 'center',
    }).setOrigin(0.5);

    // Asset loading text (shows current file being loaded)
    this.assetText = this.add.text(centerX, barY + BAR_HEIGHT + 50, '', {
      fontFamily: LOADING_BODY_FONT,
      fontSize: '10px',
      color: '#556677',
      align: 'center',
    }).setOrigin(0.5);

    // Listen for progress updates
    this.load.on('progress', (value: number) => {
      this.updateProgressBar(value, barX, barY, barWidth);
    });

    this.load.on('fileprogress', (file: Phaser.Loader.File) => {
      if (this.assetText) {
        this.assetText.setText(file.key);
      }
    });

    this.load.on('complete', () => {
      if (this.assetText) {
        this.assetText.setText('Complete!');
      }
    });
  }

  private updateProgressBar(
    value: number,
    barX: number,
    barY: number,
    barWidth: number,
  ): void {
    if (!this.progressBar || !this.percentText) {
      return;
    }

    this.progressBar.clear();
    this.progressBar.fillStyle(BAR_FILL_COLOR, 1);
    this.progressBar.fillRect(barX, barY, barWidth * value, BAR_HEIGHT);

    const percent = Math.round(value * 100);
    this.percentText.setText(`${percent}%`);
  }

  private cleanupLoadingScreen(): void {
    this.progressBg?.destroy();
    this.progressBar?.destroy();
    this.percentText?.destroy();
    this.loadingText?.destroy();
    this.assetText?.destroy();
    this.progressBg = null;
    this.progressBar = null;
    this.percentText = null;
    this.loadingText = null;
    this.assetText = null;
  }

  private loadGameAssets(): void {
    // Sprite images
    this.load.image('pacman', 'assets/images/pacman.png');
    this.load.image('ghost-red', 'assets/images/ghost-red.png');
    this.load.image('ghost-purple', 'assets/images/ghost-purple.png');
    this.load.image('ghost-yellow', 'assets/images/ghost-yellow.png');
    this.load.image('ghost-blue', 'assets/images/ghost-blue.png');
    this.load.image('ghost-frightened', 'assets/images/ghost-frightened.png');
    this.load.image('dot', 'assets/images/dot.png');
    this.load.image('power-pill', 'assets/images/power-pill.png');
    this.load.image('wall-tile', 'assets/images/wall-tile.png');
    this.load.image('empty-tile', 'assets/images/empty-tile.png');
    this.load.image('fruit-cherry', 'assets/images/fruit-cherry.png');
    this.load.image('fruit-strawberry', 'assets/images/fruit-strawberry.png');
    this.load.image('fruit-orange', 'assets/images/fruit-orange.png');
    this.load.image('fruit-apple', 'assets/images/fruit-apple.png');
    this.load.image('fruit-melon', 'assets/images/fruit-melon.png');
  }
}
